import axios from "axios";
export const getProfileLocations = async params => (await api.get('/api/profile/locations', { params })).data.options;
export const parseResumeProfile = async id => (await api.post(`/api/resumes/${id}/parse-profile`)).data.profile;
export const getSignedInUser = async () => (await api.get('/api/auth/me')).data.user;
export const saveOnboarding = async state => (await api.patch('/api/auth/onboarding',state)).data.onboarding;
export const getFilterPresets = async () => (await api.get('/api/filter-presets')).data.presets;
export const saveFilterPreset = async (name, filters) => (await api.post('/api/filter-presets', { name, filters })).data.preset;
export const updateFilterPreset = async (id, name, filters) => (await api.put(`/api/filter-presets/${id}`, { name, filters })).data.preset;
export const deleteFilterPreset = async id => (await api.delete(`/api/filter-presets/${id}`)).data.preset;
export const getJobPlatforms = async () => (await api.get('/api/job-postings/platforms')).data.platforms;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({
    baseURL: BACKEND_URL,
    withCredentials: true,
});

export const assessResumeForJob = async values => (await api.post('/api/resume-enhancements/assess', values, {timeout: 12000})).data;
export const getResumeEnhancement = async id => (await api.get(`/api/resume-enhancements/${id}`)).data.draft;
export const updateEnhancementReview = async (id,text) => api.patch(`/api/resume-enhancements/${id}/review`,{text});
export const getResumeEnhancements = async () => (await api.get('/api/resume-enhancements')).data;
export const setEnhancementReminders = async disabled => api.patch('/api/resume-enhancements/preferences', { disabled });
export const dismissResumeEnhancement = async id => api.post(`/api/resume-enhancements/${id}/dismiss`);
export const generateResumeEnhancement = async (id, options) => (await api.post(`/api/resume-enhancements/${id}/generate`, options)).data.draft;
export const createResumeRevision = async (id, values) => (await api.post(`/api/resume-enhancements/${id}/revisions`, values)).data.draft;
export const saveResumeEnhancement = async (id, values) => (await api.post(`/api/resume-enhancements/${id}/save`, values)).data.resume;
export const downloadResumeEnhancement = async (id, values) => api.post(`/api/resume-enhancements/${id}/download`, values, { responseType: 'blob' });
export const clearJobResumeSelection = async job => api.delete('/api/resume-enhancements/selection', { data: { job } });

const getActiveJobPostings = async ({
    query = "software engineer",
    location = "",
    refresh = false,
    page = 1,
    limit = 30,
    company = "",
    excludeCompany = "",
    remoteOnly = false,
    keywords = "",
    excludeFocuses = [],
    excludeEligibility = [],
    excludeJobTypes = [],
    applicationState = "all",
    postedWithin = "all",
    locations = [],
    excludeLevels = [],
    includeLevels = [],
    excludePlatforms = [],
    matchLevel = "all",
} = {}) => {
    const res = await api.get(
        "/api/job-postings",
        {
            params: {
                query,
                location,
                refresh,
                page,
                limit,
                company,
                excludeCompany,
                remoteOnly,
                keywords,
                excludeFocuses: excludeFocuses.join(","),
                excludeEligibility: excludeEligibility.join(","),
                excludeJobTypes: excludeJobTypes.join(","),
                applicationState,
                postedWithin,
                locations: locations.join(","),
                excludeLevels: excludeLevels.join(","),
                includeLevels: includeLevels.join(","),
                excludePlatforms: excludePlatforms.join(","),
                matchLevel,
            },
        }
    );

    return res.data;
};

const getJobPosting = async id => (await api.get(`/api/job-postings/detail/${encodeURIComponent(id)}`)).data.job;

const addCareerSource = async input => {
    const res = await api.post("/api/job-postings/sources", { input });
    return res.data;
};
const previewCareerSource = async input => (
    await api.post("/api/job-postings/sources/preview", { input })
).data.preview;
const getCareerSourceDiscovery = async id => (
    await api.get(`/api/job-postings/sources/discovery/${id}`)
).data.discovery;
const getCareerSources = async () => (await api.get("/api/job-postings/sources")).data.sources;
const setJobPreference = async job => (await api.post("/api/job-preferences", job)).data.job;
const getJobPreferences = async state => (await api.get("/api/job-preferences", { params:{state} })).data.jobs;
const deleteJobPreference = async id => { await api.delete(`/api/job-preferences/${id}`); };
const submitFeedback = async data => (await api.post("/api/feedback",data)).data.feedback;
const getFeedback = async () => (await api.get("/api/feedback")).data.feedback;
const updateFeedback = async (id,data) => (await api.patch(`/api/feedback/${id}`,data)).data.feedback;
const deleteFeedback = async id => { await api.delete(`/api/feedback/${id}`); };
const createManualApplication = async data => (await api.post("/api/job-applications/manual",data)).data.application;
const reportJob = async data => (await api.post("/api/job-moderation/reports",data)).data.report;
const getJobReportStatus = async jobUrl => (await api.get("/api/job-moderation/reports/status",{params:{jobUrl}})).data.reported;
const getJobModeration = async () => (await api.get("/api/job-moderation")).data.jobs;
const updateJobModeration = async data => (await api.put("/api/job-moderation",data)).data.job;
const getJobMarketAnalytics = async () => (await api.get("/api/job-analytics/market")).data.snapshot;
const refreshJobMarketAnalytics = async () => (await api.post("/api/job-analytics/market/refresh")).data.snapshot;
const getUserProfile = async () => (await api.get("/api/profile")).data.profile;
const updateUserProfileSection = async (section, value) => (await api.patch("/api/profile", { section, value })).data.profile;
const getResumes = async () => (await api.get("/api/resumes")).data.resumes;
const createResume = async formData => (await api.post("/api/resumes", formData)).data.resume;
const updateResume = async (id, values) => (await api.patch(`/api/resumes/${id}`, values)).data.resume;
const setPrimaryResume = async id => (await api.post(`/api/resumes/${id}/primary`)).data.resume;
const exportResume = async id => api.get(`/api/resumes/${id}/download`, { responseType: "blob" });
const deleteResume = async id => { await api.delete(`/api/resumes/${id}`); };
const getAiAutofillReviews = async () => (await api.get("/api/ai-autofill/reviews")).data.reviews;
const getAiAutofillReview = async id => (await api.get(`/api/ai-autofill/reviews/${id}`)).data.review;
const createExtensionPairingCode = async () => (await api.post("/api/extension/pair")).data.pairing;
const getExtensionConnections = async () => (await api.get("/api/extension/connections")).data.connections;
const revokeExtensionConnection = async id => { await api.delete(`/api/extension/connections/${id}`); };

const getJobApplicationHistory = async ({
    status = "",
    search = "",
    page = 1,
    limit = 12,
    dateRange = "all",
} = {}) => {
    const res = await api.get("/api/job-applications", {
        params: { status, search, page, limit, dateRange },
    });
    return res.data;
};

const getJobApplicationSummary = async () => {
    const res = await api.get("/api/job-applications/summary");
    return res.data.summary;
};

const updateJobApplication = async (id, changes) => {
    const res = await api.patch(`/api/job-applications/${id}`, changes);
    return res.data.application;
};

const confirmJobApplication = async application => {
    const res = await api.post("/api/job-applications/confirm", application);
    return res.data.application;
};

const deleteJobApplication = async id => {
    await api.delete(`/api/job-applications/${id}`);
};

export {
    getActiveJobPostings,
    getJobPosting,
    addCareerSource,
    previewCareerSource,
    getCareerSourceDiscovery,
    getCareerSources,
    setJobPreference,
    getJobPreferences,
    deleteJobPreference,
    submitFeedback,
    getFeedback,
    updateFeedback,
    deleteFeedback,
    createManualApplication,
    getJobApplicationHistory,
    getJobApplicationSummary,
    updateJobApplication,
    confirmJobApplication,
    deleteJobApplication,
    reportJob,
    getJobReportStatus,
    getJobModeration,
    updateJobModeration,
    getJobMarketAnalytics,
    refreshJobMarketAnalytics,
    getUserProfile,
    updateUserProfileSection,
    getResumes,
    createResume,
    updateResume,
    setPrimaryResume,
    exportResume,
    deleteResume,
    getAiAutofillReviews,
    getAiAutofillReview,
    createExtensionPairingCode,
    getExtensionConnections,
    revokeExtensionConnection,
};
